var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Vue from 'vue';
import TreeLookup from 'src/components/LookupDialogs/TreeLookup/TreeLookup.vue';
import MainLookupDialog from 'src/components/LookupDialogs/MainLookupDialog.vue';
import TimePicker from 'src/components/LookupDialogs/TimePicker.vue';
import getDisplayValue from 'src/components/GenericTable/utils/getDisplayValue';
import openValueSelectorDialog from 'src/components/GenericTable/utils/openValueSelectorDialog';
import { fromColumnToStrictColumn, getCellProps } from 'src/types';
import { isEqual, omit } from 'lodash';
import getter from 'src/utils/entityOperations/getter';
import fetchRecords from 'src/utils/entityOperations/fetchRecords';
import updateRecords from 'src/utils/entityOperations/updateRecords';
import createRecords from 'src/utils/entityOperations/createRecords';
import InfoIcon from 'src/components/UtilityComponents/InfoIcon.vue';
import DateInput from 'src/components/Dates/DateInput.vue';
import getCurrentRecord from 'src/utils/entityOperations/getCurrentRecord';
import patchCurrentRecord from 'src/utils/entityOperations/patchCurrentRecord';
import DateTimeInput from 'src/components/Dates/DateTimeInput.vue';
import isDevelopmentEnvironment from 'src/utils/developer/isDevEnvironment';
import returnIfDev from 'src/utils/developer/returnIfDev';
import TimeInput from '../TimeInput.vue';
import { goToPreviousMode } from 'src/store/mode';
import getValue from '../GenericTable/utils/getValue';
import { invokeOrIdentity, invokeOrFalse } from 'src/utils';
import isDefined from 'src/utils/filterFunctions/isDefined';
import arrayHasMembers from 'src/utils/arrayOperations/arrayHasMembers';
import notUndefined from 'src/utils/filterFunctions/notUndefined';
import Button from '../Button/Button';
import { notEmpty } from 'src/utils/filterFunctions/notEmpty';
export default Vue.extend({
    name: 'Form',
    components: { TreeLookup, MainLookupDialog, TimePicker, InfoIcon, DateInput, DateTimeInput, TimeInput, Button },
    props: {
        entityProp: { type: String },
        modeProp: { type: String },
        unconditionallyShowTheseColumnsInThisOrderProp: { type: Array },
        columnsProp: { type: Array },
        value: { type: Object },
        useIdToFetchRowProp: { type: [String, Number] },
        useStandardEntityOperationForUpdateProp: { type: Boolean },
        executeAfterRecordCreation: { type: Function },
        executeOnSubmit: { type: Function },
        executeOnCancel: { type: Function },
        executeOnInput: { type: Function },
        rowValidationRules: { type: Array }
    },
    data() {
        return {
            record: {},
            shadowRecord: {},
            ignoreRecordChangeOnce: false
        };
    },
    computed: {
        ctx() { return this; },
        columnsToShow() {
            const checker = (col) => {
                if (col.hideWhenDisplayingInNonTable) {
                    return false;
                }
                if (typeof col.cellContentIsShown === 'function') {
                    return col.cellContentIsShown({ row: this.record, col });
                }
                else {
                    return true;
                }
            };
            return this.columns.filter(checker);
        },
        columns() {
            let cols = [];
            if (this.columnsProp) {
                cols = this.columnsProp
                    .map(fromColumnToStrictColumn);
            }
            else if (this.entityProp) {
                cols = getter(this.entityProp, 'columns');
            }
            else {
                console.error('Need either columnsProp or entityProp. Present props follow');
                console.error(this.$props);
            }
            if (this.unconditionallyShowTheseColumnsInThisOrderProp && arrayHasMembers(this.unconditionallyShowTheseColumnsInThisOrderProp)) {
                cols = this.unconditionallyShowTheseColumnsInThisOrderProp.map(colName => cols.find(col => col.name === colName));
            }
            cols.forEach(col => {
                if (!col.field)
                    col.field = col.name;
            });
            cols = cols.filter(col => {
                if (this.modeProp === 'viewRecord' && col.hideWhenDisplaying)
                    return false;
                if (this.modeProp === 'createRecord' && (col.hideWhenCreating || col.hideWhenCreatingAndEditing))
                    return false;
                if (this.modeProp === 'editRecord' && (col.hideWhenEditing || col.hideWhenCreatingAndEditing))
                    return false;
                return true;
            });
            return cols.filter(notUndefined);
        },
        currentObjectInStore() {
            return getCurrentRecord(this.entityProp);
        },
        validationErrors() {
            const { rowValidationRules } = this;
            if (!rowValidationRules || !arrayHasMembers(rowValidationRules))
                return [];
            return rowValidationRules.map((rule) => (rule === null || rule === void 0 ? void 0 : rule.isValid(this.shadowRecord)) ? '' : typeof rule.errorMessage === 'string' ? rule.errorMessage : rule.errorMessage.text).filter(notEmpty);
        }
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(newVal) {
                let record = Object.assign({}, newVal);
                if (this.modeProp === 'createRecord') {
                    this.columns.forEach(col => {
                        if (col.defaultValueWhenCreating !== undefined && record[col.field] === undefined) {
                            record = Object.assign(Object.assign({}, record), { [col.field]: invokeOrIdentity(col.defaultValueWhenCreating) });
                        }
                    });
                }
                if (!isEqual(this.record, record))
                    this.setRecord(record);
            }
        },
        record: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (this.entityProp && !isEqual(newVal, oldVal) && !isEqual(newVal, getCurrentRecord(this.entityProp))) {
                    patchCurrentRecord(this.entityProp, newVal);
                    this.shadowRecord = Object.assign({}, newVal);
                }
            }
        },
        currentObjectInStore: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (!isEqual(newVal, oldVal) && !isEqual(newVal, this.record))
                    this.setRecord(Object.assign(Object.assign({}, this.record), newVal));
            }
        },
        useIdToFetchRowProp: {
            immediate: true,
            handler(newVal) {
                var _a;
                return __awaiter(this, void 0, void 0, function* () {
                    if (newVal && !this.value) {
                        if (!this.entityProp) {
                            console.error('If useIdToFetchRowProp is set, entityProp is required.');
                        }
                        else {
                            const response = yield fetchRecords(this.entityProp, { payload: { filter_conditions: [{ field: 'id', predicate: 'equals', value: newVal }] }, fetchAllColumnsWhenFetching: true, onceFetchedApplyAsPartialRefresh: true });
                            if (response) {
                                this.ignoreRecordChangeOnce = true;
                                this.setRecord((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a[0]);
                            }
                        }
                    }
                });
            }
        },
        useStandardEntityOperationForUpdateProp: {
            immediate: true,
            handler(newVal) {
                if (newVal && !this.entityProp)
                    console.error('If useStandardEntityOperationForUpdateProp is set, entityProp is required.');
            }
        }
    },
    methods: {
        omit,
        getDropdownSelectorProps(col) {
            const { record: row, getCellProps } = this;
            function getDropdownSelectorOptions(col, row) {
                const options = col.dropdownSelectorOptions;
                const result = options ? (typeof options === 'function' ? options({ row, col }) : options) : [];
                return result;
            }
            const props = {
                value: Number(this.getRecordFieldValue(row, col)),
                options: getDropdownSelectorOptions(col, row),
                emitValue: true,
                mapOptions: true,
                borderless: true,
                dense: true,
                filled: true,
                stackLabel: true,
                label: String(invokeOrIdentity(col.label) || col.name),
                popupContentStyle: 'white-space:nowrap',
                clearable: invokeOrIdentity(col.showUnsetValueButton, getCellProps(col)),
            };
            const props2 = Object.assign(Object.assign({}, props), (col.dropdownSelectorProps || {}));
            return props2;
        },
        handleComponentInputEvent(col, emittedValue) {
            return __awaiter(this, void 0, void 0, function* () {
                const newVal = col.transformInputToValue ? col.transformInputToValue({ col, row: this.record }, emittedValue) : emittedValue;
                col.limitSavingToOnlyUsingSaveButtonOnTheField
                    ? this.mutateShadowRecord(col, newVal || emittedValue)
                    : yield this.mutateRecord(col, newVal || emittedValue);
            });
        },
        getRecordFieldValue(record, col) {
            if (col.valueGetter)
                return col.valueGetter({ row: record, col });
            return record[col.field];
        },
        invokeOrFalse,
        invokeOrIdentity,
        getComponentProps(col) {
            return invokeOrIdentity(col.componentProps2, this.getCellProps(col)) || invokeOrIdentity(col.componentProps, col, this.record, {});
        },
        getComponentEventListeners(col) { return Object.assign({}, invokeOrIdentity(col.componentEventListeners, this.getCellProps(col))); },
        getCellProps(col) { return getCellProps({ col, row: this.record }); },
        cellIsEditable(col) {
            const { record, modeProp } = this;
            const cellProps = getCellProps({ col, row: record });
            if (modeProp === 'editRecord') {
                if (isDefined(col.editable))
                    return !!invokeOrIdentity(col.editable, cellProps);
                if (isDefined(col.inlineEdit))
                    return !!invokeOrIdentity(col.inlineEdit, cellProps);
                return true;
            }
            else if (modeProp === 'createRecord') {
                if (isDefined(col.notEditableWhenCreating))
                    return !!invokeOrIdentity(col.notEditableWhenCreating, cellProps);
                return true;
            }
            else
                return false;
        },
        getColType(col) {
            return col.isTextarea ? 'textarea' : 'text';
        },
        isDevelopmentEnvironment,
        returnIfDev,
        headerTooltip(col) {
            return invokeOrIdentity(col.headerInfoTip, []) || '';
        },
        getDropdownSelectorOptions(col) {
            const options = col.dropdownSelectorOptions;
            const result = options ? (typeof options === 'function' ? options({ row: this.record, col }) : options) : [];
            return result;
        },
        shadowRecordDivergedFromRecord(col) {
            const field = col.field;
            return String(this.record[field] || 0) !== String(this.shadowRecord[field] || 0);
        },
        getPropsOfInputComponentForInlineEdit(col) {
            const result = {
                debounce: 500,
                value: getValue(col, this.shadowRecord),
                standout: this.shadowRecordDivergedFromRecord(col),
                autogrow: col.autogrow,
                autofocus: col.autofocus,
                filled: true,
                dense: true,
                stackLabel: true,
                label: col.label
            };
            return result;
        },
        cellClickHandler(cellProps, e) {
            const col = cellProps.col;
            if (col && col.cellClickHandler && typeof col.cellClickHandler === 'function') {
                col.cellClickHandler(cellProps, {}, e);
            }
        },
        getDisplayValue,
        /** Is used to call from outside */
        setRecord(record) {
            this.record = Object.assign({}, record);
            this.shadowRecord = Object.assign({}, record);
        },
        mutateRecord(col, value) {
            return __awaiter(this, void 0, void 0, function* () {
                this.mutateRecord_(String(col.field || col.name), value);
            });
        },
        mutateRecord_(key, value) {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                this.$set(this.record, key, value);
                this.$emit('input', this.record);
                if (this.executeOnInput)
                    this.executeOnInput(this.record, this.shadowRecord);
                this.shadowRecord = Object.assign({}, this.record);
                if (this.modeProp !== 'createRecord' && this.useStandardEntityOperationForUpdateProp && this.entityProp) {
                    const response = yield updateRecords(this.entityProp, [this.record]);
                    if (response.result === 'success') {
                        const oldRecord = this.record;
                        const newRecord = ((_a = response.data) === null || _a === void 0 ? void 0 : _a[0]) || {};
                        if (!isEqual(newRecord, oldRecord)) {
                            this.setRecord(newRecord);
                            this.$emit('remote_record_updated', newRecord);
                        }
                    }
                }
            });
        },
        mutateShadowRecord(col, value) {
            this.mutateShadowRecord_(String(col.field || col.name), value);
        },
        mutateShadowRecord_(key, value) {
            this.$set(this.shadowRecord, key, value);
        },
        mutateRecordWithShadowState(key) {
            this.mutateRecord_(key, this.shadowRecord[key]);
        },
        mutateShadowRecordWithRecordState(key) {
            this.mutateShadowRecord_(key, this.record[key]);
        },
        createRecord() {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield createRecords(this.entityProp, [this.record]);
                if (response) {
                    this.setRecord(((_a = response.data) === null || _a === void 0 ? void 0 : _a[0]) || {});
                    if (this.executeAfterRecordCreation) {
                        this.executeAfterRecordCreation(this.record);
                    }
                }
                return ((_b = response.data) === null || _b === void 0 ? void 0 : _b[0]) || {};
            });
        },
        updateRecord() {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield updateRecords(this.entityProp, [this.record]);
                if (response) {
                    this.setRecord(((_a = response.data) === null || _a === void 0 ? void 0 : _a[0]) || {});
                }
                return ((_b = response.data) === null || _b === void 0 ? void 0 : _b[0]) || {};
            });
        },
        handleClick(e, col) {
            if (col.isTime || col.isDate || col.references || col.complexlyReferences) {
                if (this.cellIsEditable(col)) {
                    this.tryToOpenValueSelectorDialog(e, this.getCellProps(col), /* valueChangeCallback: */ this.handleCellChange, this);
                }
            }
        },
        handleCellChange(e, props) {
            const { row, col } = props;
            const field = col.sourceField ? col.sourceField(props) : col.field;
            const oldVal = String(row[field] || '');
            let newVal = '';
            newVal = (e && e.srcElement) ? String(e.srcElement.value) : e;
            if (oldVal === newVal)
                return false;
            if (String(newVal).endsWith('%')) {
                newVal = String(parseFloat(newVal.substring(0, newVal.length - 1)) / 100);
            }
            else if (parseFloat(newVal) > 1 && (col.isPercent || (typeof col.cellIsPercent === 'function' && col.cellIsPercent(props)))) {
                newVal = String(parseFloat(newVal) / 100);
            }
            else if (col.isNumeric) {
                newVal = String(parseFloat(newVal));
            }
            this.mutateRecord_(String(field), newVal);
        },
        tryToOpenValueSelectorDialog(e, cellProps, valueChangeCallback, ctx) {
            const { col } = cellProps;
            if (col.isTime || col.isDate || col.references || col.complexlyReferences || (col.cellReferences && col.cellReferences({ row: this.record, col }))) {
                e.stopPropagation();
            }
            openValueSelectorDialog(cellProps, valueChangeCallback, ctx);
        },
        cancel() {
            goToPreviousMode();
            this.$destroy();
        },
        getCellClasses(col) {
            const errors = this.getCellRelatedValidationErrors(col);
            return errors.length > 0 ? 'validation-error rownded' : '';
        },
        getCellRelatedValidationErrors(col) {
            const errors = typeof col.getCellRelatedValidationErrors === 'function'
                ? col.getCellRelatedValidationErrors()
                : [];
            return errors;
        }
    }
});
